import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { pipe, get, toLower, includes, filter, isEmpty, find, matches, propEq, groupBy, reject, has, placeholder, isNil, defer, entries, identity } from "lodash/fp";
import { singular } from "pluralize";
import { useId } from "@reach/auto-id";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import { useOnClickOutside } from "../../hooks/use-on-click-outside.es.js";
import "@reach/rect";
import { useScrollIntoView } from "../../hooks/use-scroll-into-view.es.js";
import { SelectList } from "./select-list.es.js";
import { SelectWrapper, SelectInner, SelectMenu, SelectPopover, SelectSearch, SelectGroup, SelectGroupHeading, SelectItem, SelectGroupPlaceholder } from "./select.styles.es.js";
import { AnimatedPopover } from "../animated-popover/animated-popover.es.js";
import { Input } from "../input/input.es.js";
import { LabelText } from "../label-text/label-text.es.js";
import { Message } from "../message/message.es.js";
import { SelectButton } from "../select-button/select-button.es.js";
import { Stack } from "../stack/stack.es.js";
import { deprecated } from "../../prop-types-validators/deprecated.es.js";
const propTypes = {
  appearance: PropTypes.oneOf(["bare", "cell", "grid", "default"]),
  autoFocus: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  defaultQuery: PropTypes.string,
  disabled: PropTypes.bool,
  emptyText: deprecated(
    PropTypes.string,
    "Use itemsLabel prop or overrideEmptyText prop instead."
  ),
  groupKey: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  groupVariant: PropTypes.oneOf(["divider", "header"]),
  initialScrollPosition: PropTypes.number,
  itemKey: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  itemsFilter: PropTypes.func,
  itemsLabel: PropTypes.string,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  magnitude: PropTypes.oneOf(["small", "medium", "large"]),
  menuHeight: PropTypes.number,
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  offset: PropTypes.number,
  onChange: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  overrideEmptyText: PropTypes.string,
  overrideNoMatchText: PropTypes.string,
  overridePlaceholder: PropTypes.string,
  overrideSearchText: PropTypes.string,
  placeholder: PropTypes.string,
  placeholderText: deprecated(
    PropTypes.string,
    "Use itemsLabel prop or overridePlaceholder prop instead."
  ),
  renderGroupHeading: PropTypes.func,
  renderItem: PropTypes.func,
  renderSelected: PropTypes.func,
  requiredGroups: PropTypes.arrayOf(PropTypes.string),
  returnEntireItem: PropTypes.bool,
  search: PropTypes.bool,
  searchText: deprecated(
    PropTypes.string,
    "Use itemsLabel prop or overrideSearchText prop instead."
  ),
  tone: PropTypes.oneOf(["critical", "neutral"]),
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.object,
    PropTypes.string
  ]),
  valueKey: PropTypes.string,
  wrap: PropTypes.bool
};
const defaultProps = {
  appearance: "default",
  autoFocus: false,
  defaultExpanded: false,
  defaultQuery: "",
  disabled: false,
  emptyText: void 0,
  groupKey: void 0,
  groupVariant: "header",
  initialScrollPosition: 0,
  itemKey: void 0,
  itemsFilter: void 0,
  itemsLabel: "items",
  label: void 0,
  labelKey: "label",
  magnitude: "large",
  menuHeight: 240,
  message: void 0,
  offset: 4,
  onChange: void 0,
  overrideEmptyText: void 0,
  overrideNoMatchText: void 0,
  overridePlaceholder: void 0,
  overrideSearchText: void 0,
  placeholder: void 0,
  placeholderText: void 0,
  renderGroupHeading: identity,
  renderItem: void 0,
  renderSelected: void 0,
  requiredGroups: [],
  returnEntireItem: false,
  search: false,
  searchText: void 0,
  tone: "neutral",
  value: void 0,
  valueKey: "value",
  wrap: false
};
const Select = forwardRef(
  ({
    appearance,
    autoFocus,
    defaultExpanded,
    defaultQuery,
    disabled,
    emptyText,
    groupKey,
    groupVariant,
    initialScrollPosition,
    itemKey: itemKeyProp,
    itemsFilter: itemsFilterProp,
    itemsLabel,
    label,
    labelKey,
    magnitude,
    menuHeight,
    message,
    offset,
    onChange,
    options,
    overrideEmptyText,
    overrideNoMatchText,
    overridePlaceholder,
    overrideSearchText,
    placeholder: placeholder$1,
    placeholderText,
    searchText,
    renderGroupHeading,
    renderItem: renderItemProp,
    renderSelected: renderSelectedProp,
    requiredGroups,
    returnEntireItem,
    search,
    tone,
    value,
    valueKey,
    wrap
  }, forwardedRef) => {
    const id = useId();
    const buttonRef = useRef();
    const highlightedRef = useRef();
    const listRef = useRef();
    const menuRef = useRef();
    const [isExpanded, setExpanded] = useState(defaultExpanded);
    const [highlightedIndex, setHighlightedIndex] = useState(void 0);
    const [query, setQuery] = useState(defaultQuery);
    const itemsFilter = useMemo(() => {
      if (itemsFilterProp) {
        return itemsFilterProp;
      }
      return (inputValue) => pipe(get(labelKey), toLower, includes(toLower(inputValue)));
    }, [itemsFilterProp, labelKey]);
    const items = useMemo(
      () => filter(itemsFilter(query), options),
      [itemsFilter, options, query]
    );
    const selectedItem = useMemo(() => {
      if (returnEntireItem) {
        return !isEmpty(value) && find(matches(value), options);
      }
      return find(propEq(valueKey, value), options);
    }, [returnEntireItem, options, value, valueKey]);
    const groupedItems = useMemo(() => {
      if (!groupKey) {
        return [items];
      }
      return groupBy(groupKey, items);
    }, [items, groupKey]);
    const missingGroups = useMemo(
      () => reject(has(placeholder, groupedItems), requiredGroups),
      [groupedItems, requiredGroups]
    );
    const showGroupHeading = Boolean(groupKey) && groupVariant === "header";
    const itemKey = typeof itemKeyProp === "function" ? itemKeyProp : get(itemKeyProp || valueKey);
    const renderItem = renderItemProp || get(labelKey);
    const renderSelected = renderSelectedProp || renderItem;
    const handleBlur = useCallback(({ currentTarget, relatedTarget }) => {
      if (!currentTarget.contains(relatedTarget) && !buttonRef.current.contains(relatedTarget)) {
        setExpanded(false);
      }
    }, []);
    const handleChange = useCallback(
      (item) => {
        if (item === selectedItem) {
          setExpanded(false);
        } else {
          onChange(returnEntireItem ? item : item[valueKey]);
        }
        buttonRef.current.focus();
      },
      [onChange, returnEntireItem, selectedItem, valueKey]
    );
    const handleKeyDown = useCallback(
      (event) => {
        switch (event.key) {
          case "ArrowDown":
            event.stopPropagation();
            setHighlightedIndex(
              (x) => x === void 0 ? 0 : Math.min(x + 1, items.length - 1)
            );
            break;
          case "ArrowUp":
            event.stopPropagation();
            setHighlightedIndex(
              (x) => x === void 0 ? 0 : Math.max(0, x - 1)
            );
            break;
          case "Enter":
            event.stopPropagation();
            if (!isNil(highlightedIndex)) {
              handleChange(items[highlightedIndex]);
            }
            break;
          case "Escape":
            event.stopPropagation();
            setExpanded(false);
            buttonRef.current.focus();
            break;
        }
      },
      [handleChange, highlightedIndex, items]
    );
    useEffect(() => {
      setHighlightedIndex(void 0);
    }, [items, isExpanded]);
    useEffect(() => {
      if (isExpanded && !search) {
        defer(() => {
          if (menuRef.current) {
            menuRef.current.focus();
          }
        });
      }
    }, [isExpanded, search]);
    useScrollIntoView(listRef, highlightedRef);
    useOnClickOutside(
      [buttonRef, menuRef],
      useCallback(() => {
        setExpanded(false);
      }, [])
    );
    useImperativeHandle(forwardedRef, () => buttonRef.current);
    return /* @__PURE__ */ jsx(
      SelectWrapper,
      {
        "aria-expanded": isExpanded,
        "aria-haspopup": "listbox",
        "aria-owns": `bungle-select-${id}-popover`,
        role: "combobox",
        children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
          label && /* @__PURE__ */ jsx(
            LabelText,
            {
              as: "label",
              id: `bungle-select-${id}-label`,
              htmlFor: `bungle-select-${id}-button`,
              children: label
            }
          ),
          /* @__PURE__ */ jsxs(SelectInner, { children: [
            /* @__PURE__ */ jsx(
              SelectButton,
              {
                ref: buttonRef,
                appearance,
                "aria-label": "toggle menu",
                autoFocus,
                "data-testid": "select-btn",
                disabled,
                expanded: isExpanded,
                id: `bungle-select-${id}-button`,
                magnitude,
                onExpand: setExpanded,
                placeholder: overridePlaceholder || placeholder$1 || placeholderText || `Select ${singular(itemsLabel)}`,
                tone,
                type: "button",
                children: selectedItem && renderSelected(selectedItem)
              }
            ),
            /* @__PURE__ */ jsx(
              AnimatedPopover,
              {
                id: `bungle-select-${id}-popover`,
                justify: "stretch",
                offset,
                open: isExpanded,
                placement: "bottom",
                targetRef: buttonRef,
                children: /* @__PURE__ */ jsx(
                  SelectMenu,
                  {
                    ref: menuRef,
                    "aria-describedby": message ? `bungle-select-${id}__message` : void 0,
                    "aria-labelledby": `bungle-select-${id}-label`,
                    "data-testid": "select-menu",
                    onBlur: handleBlur,
                    onKeyDown: handleKeyDown,
                    role: "listbox",
                    tabIndex: -1,
                    children: /* @__PURE__ */ jsxs(SelectPopover, { $menuHeight: menuHeight, children: [
                      search && /* @__PURE__ */ jsx(SelectSearch, { children: /* @__PURE__ */ jsx(
                        Input,
                        {
                          appearance: "bare",
                          "aria-autocomplete": "list",
                          "aria-controls": `bungle-select-${id}-popover`,
                          autoComplete: "off",
                          autoFocus: true,
                          id: `bungle-select-${id}-search`,
                          magnitude: "large",
                          onChange: setQuery,
                          placeholder: overrideSearchText || searchText || `Filter ${itemsLabel}…`,
                          type: "search",
                          value: query,
                          variant: "top"
                        }
                      ) }),
                      /* @__PURE__ */ jsxs(
                        SelectList,
                        {
                          ref: listRef,
                          initialScrollPosition,
                          children: [
                            entries(groupedItems).map(([groupName, group]) => /* @__PURE__ */ jsxs(
                              SelectGroup,
                              {
                                "aria-labelledby": showGroupHeading ? `select-group-heading-${groupName}` : void 0,
                                role: groupKey ? "group" : void 0,
                                $groupVariant: groupVariant,
                                children: [
                                  showGroupHeading && /* @__PURE__ */ jsx(
                                    SelectGroupHeading,
                                    {
                                      id: `select-group-heading-${groupName}`,
                                      role: "presentation",
                                      children: renderGroupHeading(groupName)
                                    }
                                  ),
                                  group.map((item) => /* @__PURE__ */ jsx(
                                    SelectItem,
                                    {
                                      ref: items.indexOf(item) === highlightedIndex ? highlightedRef : void 0,
                                      "aria-label": renderSelected && renderSelected(item),
                                      "aria-selected": item === selectedItem,
                                      "data-testid": "select-item",
                                      $highlighted: items.indexOf(item) === highlightedIndex,
                                      indent: showGroupHeading,
                                      onClick: () => handleChange(item),
                                      onMouseEnter: () => {
                                        setHighlightedIndex(items.indexOf(item));
                                      },
                                      role: "option",
                                      $wrap: wrap,
                                      children: renderItem(item)
                                    },
                                    itemKey(item)
                                  ))
                                ]
                              },
                              groupName
                            )),
                            items.length === 0 ? /* @__PURE__ */ jsx(SelectGroup, { role: "presentation", children: /* @__PURE__ */ jsx(SelectGroupPlaceholder, { role: "presentation", children: options.length === 0 ? `${overrideEmptyText || emptyText || `There are no ${itemsLabel} available.`}` : overrideNoMatchText || `There are no matching ${itemsLabel}.` }) }) : missingGroups.map((groupName) => /* @__PURE__ */ jsx(
                              SelectGroup,
                              {
                                role: "presentation",
                                $groupVariant: groupVariant,
                                children: /* @__PURE__ */ jsx(SelectGroupPlaceholder, { role: "presentation", children: overrideEmptyText || `There are no ${itemsLabel} available.` })
                              },
                              groupName
                            ))
                          ]
                        }
                      )
                    ] })
                  }
                )
              }
            )
          ] }),
          message && /* @__PURE__ */ jsx(Message, { id: `bungle-select-${id}__message`, tone, children: message })
        ] })
      }
    );
  }
);
Select.propTypes = propTypes;
Select.defaultProps = defaultProps;
export {
  Select
};
